import React, { FunctionComponent, useState } from "react"
import { useTranslation } from "react-i18next"

import { Flex } from "components/Flex"
import Modal from "components/Modal/Modal"
import Button from "components/Button/Button"
import Radio from "components/Radio/Radio"
import Text from "components/Text/Text"
import FormField from "components/FormField/FormField"
import { AssetOwnershipType } from "models/Asset"

export interface ICopyAssetOwnershipModalProps {
    /** Initial condition of the source asset, preselected for the copies. */
    sourceIsSecondhand: boolean
    onConfirm(ownershipType: AssetOwnershipType, isSecondhand: boolean): void
    onClose(): void
}

/**
 * Ownership and initial-condition selection shown before the copy dialog
 * opens.
 *
 * The copy target may have a different ownership than its source, and the
 * choice drives both dropdown matrices of the copy dialog, so it has to be made
 * up front. Neither ownership option is preselected — the user must pick one to
 * proceed. The initial condition is preselected with the source's value and
 * applies to every copy.
 */
const CopyAssetOwnershipModal: FunctionComponent<React.PropsWithChildren<ICopyAssetOwnershipModalProps>> = ({
    sourceIsSecondhand,
    onConfirm,
    onClose,
}) => {
    const { t } = useTranslation()
    const [ownershipType, setOwnershipType] = useState<AssetOwnershipType | null>(null)
    const [isSecondhand, setIsSecondhand] = useState(sourceIsSecondhand)

    return (
        <Modal
            title={t('copyAsset.copyAsset')}
            onClose={onClose}
            maxWidth="560px"
            footer={
                <Flex justifyContent="flex-end"
                    alignItems="center"
                    gap="2.4rem">
                    <Button
                        theme="secondary"
                        label={t('common.cancel')}
                        onClick={onClose}
                    />
                    <Button
                        label={t('common.continue')}
                        disabled={!ownershipType}
                        onClick={() => ownershipType && onConfirm(ownershipType, isSecondhand)}
                    />
                </Flex>
            }>
            <Text type="p">{t('copyAsset.selectOwnershipDescr')}</Text>
            <Flex gap="2.4rem" alignItems="center" wrap="wrap" className="mt-4">
                <Radio
                    label={t('lmidModal.purchasedAsset')}
                    value={AssetOwnershipType.purchased}
                    checked={ownershipType === AssetOwnershipType.purchased}
                    onChange={(val: AssetOwnershipType) => setOwnershipType(val)}
                    name="copyAssetOwnershipType"
                />
                <Radio
                    label={t('lmidModal.borrowedAsset')}
                    value={AssetOwnershipType.borrowed}
                    checked={ownershipType === AssetOwnershipType.borrowed}
                    onChange={(val: AssetOwnershipType) => setOwnershipType(val)}
                    name="copyAssetOwnershipType"
                />
            </Flex>
            <FormField label={t('asset.initialCondition')} className="mt-4">
                <Flex gap="2.4rem" alignItems="center" wrap="wrap">
                    <Radio
                        label={t('asset.newAsset')}
                        value={0}
                        checked={!isSecondhand}
                        onChange={value => setIsSecondhand(!!value)}
                        name="copyAssetIsSecondhand"
                    />
                    <Radio
                        label={t('asset.usedAsset')}
                        value={1}
                        checked={isSecondhand}
                        onChange={value => setIsSecondhand(!!value)}
                        name="copyAssetIsSecondhand"
                    />
                </Flex>
            </FormField>
        </Modal>
    )
}

export default CopyAssetOwnershipModal
